import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth, useSelectedDate, getWeeksInMonth } from "../App";
import { Trophy, Crown, Medal, Target, TrendingUp, Users, Zap, Award, ChevronDown, ChevronUp, X, Building2, Calendar, CalendarDays } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Department configurations with their metrics
const DEPARTMENTS = {
  all: { 
    label: "Teams", 
    color: "bg-slate-600",
    metrics: ["team_generated", "self_generated", "demos_held", "upgrades_closed", "new_deals_closed"]
  },
  sales: { 
    label: "Sales", 
    color: "bg-indigo-600",
    metrics: ["team_generated", "self_generated", "demos_held", "upgrades_closed", "new_deals_closed"]
  },
  csm: { 
    label: "CSM", 
    color: "bg-emerald-600",
    metrics: ["renewals", "upsells", "customer_calls", "nps_score", "churn_prevention"]
  },
  dev: { 
    label: "Dev", 
    color: "bg-purple-600",
    metrics: ["tickets_closed", "features_shipped", "bugs_fixed", "code_reviews", "deployments"]
  },
  support: { 
    label: "Support", 
    color: "bg-amber-600",
    metrics: ["tickets_resolved", "first_response_time", "customer_satisfaction", "escalations_handled", "knowledge_articles"]
  },
  marketing: { 
    label: "Marketing", 
    color: "bg-pink-600",
    metrics: ["leads_generated", "campaigns_launched", "content_published", "social_engagement", "email_opens"]
  },
};

const METRIC_LABELS = {
  // Sales
  team_generated: "Team Gen",
  self_generated: "Self Gen",
  demos_held: "Demos",
  upgrades_closed: "Upgrades",
  new_deals_closed: "Deals",
  // CSM
  renewals: "Renewals",
  upsells: "Upsells",
  customer_calls: "Calls",
  nps_score: "NPS",
  churn_prevention: "Saved",
  // Dev
  tickets_closed: "Tickets",
  features_shipped: "Features",
  bugs_fixed: "Bugs",
  code_reviews: "Reviews",
  deployments: "Deploys",
  // Support
  tickets_resolved: "Resolved",
  first_response_time: "Response",
  customer_satisfaction: "CSAT",
  escalations_handled: "Escalations",
  knowledge_articles: "KB Articles",
  // Marketing
  leads_generated: "Leads",
  campaigns_launched: "Campaigns",
  content_published: "Content",
  social_engagement: "Social",
  email_opens: "Opens",
};

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Generate year options (current year and previous 2 years)
const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

const Leaderboard = () => {
  const { token } = useAuth();
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, viewMode, setViewMode, selectedWeek, setSelectedWeek } = useSelectedDate();
  const [salespeople, setSalespeople] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedMetric, setSelectedMetric] = useState("all");
  const [loading, setLoading] = useState(true);
  const [topPerformer, setTopPerformer] = useState(null);
  const [expandedPerson, setExpandedPerson] = useState(null);
  const [personStats, setPersonStats] = useState({});

  const headers = { Authorization: `Bearer ${token}` };
  
  // Get weeks for the selected month
  const weeksInMonth = getWeeksInMonth(selectedYear, selectedMonth);

  // Get current department metrics
  const currentDeptMetrics = DEPARTMENTS[selectedDepartment]?.metrics || DEPARTMENTS.sales.metrics;

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, selectedDepartment, viewMode, selectedWeek]);

  // Fetch individual person's detailed stats when expanded
  const fetchPersonStats = async (personId) => {
    if (personStats[personId]) {
      // Already have stats, just toggle expansion
      setExpandedPerson(expandedPerson === personId ? null : personId);
      return;
    }
    
    try {
      const [monthlyRes, yearlyRes] = await Promise.all([
        axios.get(`${API}/stats/monthly/${personId}?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        axios.get(`${API}/stats/yearly/${personId}?year=${selectedYear}`, { headers }),
      ]);
      
      setPersonStats(prev => ({
        ...prev,
        [personId]: {
          monthly: monthlyRes.data,
          yearly: yearlyRes.data,
        }
      }));
      setExpandedPerson(personId);
    } catch (error) {
      console.error("Error fetching person stats:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params based on view mode
      let statsUrl = `${API}/stats/team?year=${selectedYear}&month=${selectedMonth}`;
      if (viewMode === 'weekly' && selectedWeek) {
        statsUrl += `&week=${selectedWeek}`;
      }
      
      const [peopleRes, statsRes] = await Promise.all([
        axios.get(`${API}/salespeople`, { headers }),
        axios.get(statsUrl, { headers }),
      ]);
      
      setSalespeople(peopleRes.data);
      
      // Filter by department if not "all"
      let filteredData = statsRes.data.by_salesperson || [];
      if (selectedDepartment !== "all") {
        const deptPeople = peopleRes.data.filter(p => p.department === selectedDepartment).map(p => p.id);
        filteredData = filteredData.filter(p => deptPeople.includes(p.salesperson.id));
      }
      
      // Calculate rankings based on department-specific metrics
      const rankedData = filteredData.map((person) => {
        // Get the person's department
        const personInfo = peopleRes.data.find(p => p.id === person.salesperson.id);
        const dept = personInfo?.department || "sales";
        const deptMetrics = DEPARTMENTS[dept]?.metrics || DEPARTMENTS.sales.metrics;
        
        // Revenue fields should not be summed with activity counts
        const revenueFields = ['deals_revenue', 'renewals_revenue', 'upsells_revenue'];
        
        // Calculate total from department-relevant metrics only (excluding revenue)
        let total = 0;
        deptMetrics.forEach(metric => {
          if (!revenueFields.includes(metric)) {
            total += person.totals[metric] || 0;
          }
        });
        
        return {
          ...person,
          department: dept,
          total,
        };
      }) || [];
      
      // Sort by selected metric or total
      rankedData.sort((a, b) => {
        if (selectedMetric === "all") {
          return b.total - a.total;
        }
        return (b.totals[selectedMetric] || 0) - (a.totals[selectedMetric] || 0);
      });
      
      setRankings(rankedData);
      if (rankedData.length > 0) {
        setTopPerformer(rankedData[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Re-sort when metric changes
    if (rankings.length > 0) {
      const sorted = [...rankings].sort((a, b) => {
        if (selectedMetric === "all") {
          return b.total - a.total;
        }
        return (b.totals[selectedMetric] || 0) - (a.totals[selectedMetric] || 0);
      });
      setRankings(sorted);
      setTopPerformer(sorted[0]);
    }
  }, [selectedMetric]);

  const getValue = (person) => {
    if (selectedMetric === "all") {
      return person.total;
    }
    return person.totals[selectedMetric] || 0;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) {
      return (
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-yellow-500 flex items-center justify-center shadow-lg shadow-yellow-500/30">
          <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
      );
    } else if (rank === 2) {
      return (
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-400 flex items-center justify-center shadow-lg shadow-slate-400/30">
          <Medal className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
      );
    } else if (rank === 3) {
      return (
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-700 flex items-center justify-center shadow-lg shadow-amber-700/30">
          <Medal className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-700 flex items-center justify-center">
        <span className="text-slate-300 font-bold text-sm sm:text-base">{rank}</span>
      </div>
    );
  };

  const getMetricLabel = () => {
    if (selectedMetric === "all") return "TOTAL";
    return METRIC_LABELS[selectedMetric]?.toUpperCase() || "TOTAL";
  };

  // Get department color
  const getDeptColor = (dept) => {
    return DEPARTMENTS[dept]?.color || "bg-slate-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner border-t-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="leaderboard-page">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              LEADERBOARD
            </h1>
          </div>
          {topPerformer && (
            <div className="bg-slate-800 rounded-full px-3 py-1 sm:px-4 sm:py-1.5 text-xs sm:text-sm">
              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${getDeptColor(topPerformer.department)}`}></span>
              {rankings.length} members
            </div>
          )}
        </div>

        {/* Filters Row - View Mode + Month/Year/Week + Department */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-slate-700 h-8">
            <button
              onClick={() => setViewMode('weekly')}
              className={`px-3 text-xs font-medium transition-colors ${
                viewMode === 'weekly' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 text-xs font-medium transition-colors ${
                viewMode === 'monthly' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              Monthly
            </button>
          </div>

          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[110px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="month-selector">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {MONTHS.map((month) => (
                <SelectItem key={month.value} value={String(month.value)} className="text-slate-200 text-xs">
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[80px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="year-selector">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {YEARS.map((year) => (
                <SelectItem key={year} value={String(year)} className="text-slate-200 text-xs">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Week Selector - only shown in weekly mode */}
          {viewMode === 'weekly' && weeksInMonth.length > 0 && (
            <Select value={selectedWeek} onValueChange={setSelectedWeek}>
              <SelectTrigger className="w-[160px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="week-selector">
                <CalendarDays size={12} className="mr-1" />
                <SelectValue placeholder="Select week" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {weeksInMonth.map((week) => (
                  <SelectItem key={week.value} value={week.value} className="text-slate-200 text-xs">
                    {week.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={selectedDepartment} onValueChange={(v) => { setSelectedDepartment(v); setSelectedMetric("all"); }}>
            <SelectTrigger className="w-[140px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
              <Building2 size={12} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {Object.entries(DEPARTMENTS).map(([key, dept]) => (
                <SelectItem key={key} value={key} className="text-slate-200 text-xs">
                  {dept.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Top Performer Card */}
        {topPerformer && (
          <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6 border border-slate-700" data-testid="top-performer-card">
            <div className="flex items-center gap-3 sm:gap-4 mb-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 ${getDeptColor(topPerformer.department)} rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl font-bold shadow-lg`}>
                {topPerformer.salesperson.name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-xl font-bold truncate">{topPerformer.salesperson.name?.toUpperCase()}</h2>
                <p className="text-emerald-400 text-xs sm:text-sm truncate">
                  {DEPARTMENTS[topPerformer.department]?.label || "Sales"}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-400">{topPerformer.total.toFixed(0)}</div>
                <div className="text-xs text-slate-500">TOTAL</div>
              </div>
            </div>
            
            {/* Stats Grid - Department specific */}
            <div className="flex sm:grid sm:grid-cols-5 gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
              {/* When viewing "all", show the top performer's actual department metrics */}
              {(selectedDepartment === "all" 
                ? DEPARTMENTS[topPerformer.department]?.metrics || DEPARTMENTS.sales.metrics
                : currentDeptMetrics
              ).map((metric, index) => (
                <div key={index} className="bg-slate-900/50 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center min-w-[70px] flex-shrink-0 sm:min-w-0">
                  <div className="text-emerald-400 font-bold text-base sm:text-lg">{(topPerformer.totals[metric] || 0).toFixed(0)}</div>
                  <div className="text-slate-500 text-[10px] sm:text-xs mt-0.5 whitespace-nowrap">{METRIC_LABELS[metric]}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metric Filters - Department specific */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 sm:mx-0 sm:px-0 scrollbar-hide">
          <button
            onClick={() => setSelectedMetric("all")}
            className={`flex flex-col items-center gap-1 sm:gap-1.5 min-w-[56px] sm:min-w-[72px] py-2 sm:py-3 px-2 sm:px-3 rounded-lg sm:rounded-xl border transition-all flex-shrink-0 ${
              selectedMetric === "all"
                ? "bg-slate-800 border-slate-600 text-white"
                : "bg-slate-800/30 border-slate-800 text-slate-500 hover:text-slate-300"
            }`}
            data-testid="metric-all"
          >
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-[10px] sm:text-xs font-medium">All</span>
          </button>
          {currentDeptMetrics.map((metric) => (
            <button
              key={metric}
              onClick={() => setSelectedMetric(metric)}
              className={`flex flex-col items-center gap-1 sm:gap-1.5 min-w-[56px] sm:min-w-[72px] py-2 sm:py-3 px-2 sm:px-3 rounded-lg sm:rounded-xl border transition-all flex-shrink-0 ${
                selectedMetric === metric
                  ? "bg-slate-800 border-slate-600 text-white"
                  : "bg-slate-800/30 border-slate-800 text-slate-500 hover:text-slate-300"
              }`}
              data-testid={`metric-${metric}`}
            >
              <Target className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-[10px] sm:text-xs font-medium whitespace-nowrap">{METRIC_LABELS[metric]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Rankings List */}
      <div className="space-y-2 sm:space-y-3 mb-4" data-testid="rankings-list">
        {rankings.map((person, index) => {
          const rank = index + 1;
          const value = getValue(person);
          const isExpanded = expandedPerson === person.salesperson.id;
          const stats = personStats[person.salesperson.id];
          
          return (
            <div key={person.salesperson.id} className="space-y-0">
              <div
                onClick={() => fetchPersonStats(person.salesperson.id)}
                className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all cursor-pointer hover:bg-slate-700/50 ${
                  rank === 1 
                    ? "bg-gradient-to-r from-slate-800 to-slate-800/50 border border-yellow-500/30" 
                    : "bg-slate-800/50 border border-slate-700/50"
                } ${isExpanded ? 'rounded-b-none border-b-0' : ''}`}
                data-testid={`ranking-${index}`}
              >
                {getRankBadge(rank)}
                
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-sm sm:text-lg font-bold flex-shrink-0 ${getDeptColor(person.department)}`}>
                  {person.salesperson.name?.charAt(0)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white text-sm sm:text-base truncate">
                    {person.salesperson.name}
                  </h3>
                  <p className="text-emerald-400 text-xs sm:text-sm truncate">
                    @{person.salesperson.name?.toLowerCase().replace(' ', '')}
                  </p>
                  <div className="flex gap-3 sm:gap-4 mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-slate-500">
                    {/* Show department-appropriate quick stats */}
                    {(() => {
                      const deptMetrics = DEPARTMENTS[person.department]?.metrics || DEPARTMENTS.sales.metrics;
                      const metric1 = deptMetrics[0];
                      const metric2 = deptMetrics[1];
                      return (
                        <>
                          <span>{(person.totals[metric1] || 0).toFixed(0)} {METRIC_LABELS[metric1]?.toLowerCase()}</span>
                          <span>{(person.totals[metric2] || 0).toFixed(0)} {METRIC_LABELS[metric2]?.toLowerCase()}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                <div className="text-right flex-shrink-0 flex items-center gap-2">
                  <div>
                    <div className={`text-xl sm:text-2xl font-bold ${rank <= 3 ? 'text-emerald-400' : 'text-white'}`}>
                      {value.toFixed(0)}
                    </div>
                    <div className="text-[10px] sm:text-xs text-slate-500">{getMetricLabel()}</div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Stats Panel */}
              {isExpanded && stats && (
                <div className="bg-slate-800/80 border border-slate-700/50 border-t-0 rounded-b-xl sm:rounded-b-2xl p-4 animate-fade-in" data-testid={`expanded-stats-${index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-300">Detailed Stats - {DEPARTMENTS[person.department]?.label || "Sales"}</h4>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setExpandedPerson(null); }}
                      className="text-slate-500 hover:text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  
                  {/* Stats Tabs - Department Specific */}
                  {(() => {
                    const deptMetrics = DEPARTMENTS[person.department]?.metrics || DEPARTMENTS.sales.metrics;
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* This Month */}
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <h5 className="text-xs font-medium text-amber-400 mb-2 uppercase tracking-wide">This Month</h5>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {deptMetrics.slice(0, 4).map((metric) => (
                              <div key={metric} className="flex justify-between">
                                <span className="text-slate-500">{METRIC_LABELS[metric]}</span>
                                <span className="text-white font-medium">{(stats.monthly.totals?.[metric] || 0).toFixed(0)}</span>
                              </div>
                            ))}
                            {deptMetrics[4] && (
                              <div className="flex justify-between col-span-2">
                                <span className="text-slate-500">{METRIC_LABELS[deptMetrics[4]]}</span>
                                <span className="text-emerald-400 font-bold">{(stats.monthly.totals?.[deptMetrics[4]] || 0).toFixed(0)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Year to Date */}
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <h5 className="text-xs font-medium text-purple-400 mb-2 uppercase tracking-wide">Year to Date ({selectedYear})</h5>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {deptMetrics.slice(0, 4).map((metric) => (
                              <div key={metric} className="flex justify-between">
                                <span className="text-slate-500">{METRIC_LABELS[metric]}</span>
                                <span className="text-white font-medium">{(stats.yearly.yearly_totals?.[metric] || 0).toFixed(0)}</span>
                              </div>
                            ))}
                            {deptMetrics[4] && (
                              <div className="flex justify-between col-span-2">
                                <span className="text-slate-500">{METRIC_LABELS[deptMetrics[4]]}</span>
                                <span className="text-emerald-400 font-bold">{(stats.yearly.yearly_totals?.[deptMetrics[4]] || 0).toFixed(0)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Total Summary */}
                  <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between items-center">
                    <span className="text-sm text-slate-400">Year Total Activities</span>
                    <span className="text-lg font-bold text-white">
                      {(() => {
                        const deptMetrics = DEPARTMENTS[person.department]?.metrics || DEPARTMENTS.sales.metrics;
                        if (!stats.yearly.yearly_totals) return 0;
                        return deptMetrics.reduce((sum, metric) => sum + (stats.yearly.yearly_totals[metric] || 0), 0).toFixed(0);
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {rankings.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No data available for this period</p>
            <p className="text-sm mt-1">Add some entries to see the leaderboard</p>
          </div>
        )}
      </div>

      {/* Bottom Stats Bar */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl px-3 sm:px-6 py-3 sm:py-4">
        <div className="grid grid-cols-4 gap-2 sm:gap-4">
          <div className="text-center">
            <div className="text-emerald-400 font-bold text-sm sm:text-lg">
              {rankings.reduce((sum, p) => sum + p.total, 0).toFixed(0)}
            </div>
            <div className="text-[10px] sm:text-xs text-slate-500">TEAM TOTAL</div>
          </div>
          <div className="text-center">
            <div className="text-white font-bold text-sm sm:text-lg">{rankings.length}</div>
            <div className="text-[10px] sm:text-xs text-slate-500">COMPETITORS</div>
          </div>
          <div className="text-center">
            <div className="text-amber-400 font-bold text-sm sm:text-lg">
              {rankings.reduce((sum, p) => sum + p.totals.new_deals_closed, 0).toFixed(0)}
            </div>
            <div className="text-[10px] sm:text-xs text-slate-500">TOTAL DEALS</div>
          </div>
          <div className="text-center">
            <div className="text-purple-400 font-bold text-sm sm:text-lg">
              {rankings.length > 0 ? (rankings.reduce((sum, p) => sum + p.total, 0) / rankings.length).toFixed(0) : 0}
            </div>
            <div className="text-[10px] sm:text-xs text-slate-500">AVG SCORE</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
